// Analytics tracking functions
import { collection, addDoc, getDocs, query, where, orderBy, limit, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from './config';

/**
 * Track an event
 */
export const trackEvent = async (eventType, metadata = {}) => {
  try {
    const analyticsRef = collection(db, 'analytics');
    const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}`;
    const userId = localStorage.getItem('userId') || null;
    
    // Store session ID if not exists
    if (!sessionStorage.getItem('sessionId')) {
      sessionStorage.setItem('sessionId', sessionId);
    }
    
    await addDoc(analyticsRef, {
      type: eventType,
      metadata,
      timestamp: serverTimestamp(),
      sessionId,
      userId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer || null
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error tracking event:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Track page view
 */
export const trackPageView = async (pageName, metadata = {}) => {
  return await trackEvent('page-view', {
    page: pageName,
    ...metadata
  });
};

/**
 * Track purchase
 */
export const trackPurchase = async (orderId, total, productIds = []) => {
  return await trackEvent('purchase', {
    orderId,
    total,
    productIds,
    currency: 'GBP'
  });
};

/**
 * Track email signup
 */
export const trackEmailSignup = async (source = 'unknown') => {
  return await trackEvent('email-signup', {
    source
  });
};

/**
 * Track click
 */
export const trackClick = async (elementId, elementType, metadata = {}) => {
  return await trackEvent('click', {
    elementId,
    elementType,
    ...metadata
  });
};

/**
 * Get analytics data for a date range
 */
export const getAnalytics = async (startDate, endDate, eventType = null) => {
  try {
    const analyticsRef = collection(db, 'analytics');
    let q = query(
      analyticsRef,
      where('timestamp', '>=', Timestamp.fromDate(startDate)),
      where('timestamp', '<=', Timestamp.fromDate(endDate)),
      orderBy('timestamp', 'desc')
    );
    
    if (eventType) {
      q = query(q, where('type', '==', eventType));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting analytics:', error);
    return [];
  }
};

/**
 * Get analytics summary (aggregated stats)
 */
export const getAnalyticsSummary = async (startDate, endDate) => {
  try {
    const events = await getAnalytics(startDate, endDate);
    
    const summary = {
      totalEvents: events.length,
      pageViews: events.filter(e => e.type === 'page-view').length,
      purchases: events.filter(e => e.type === 'purchase').length,
      emailSignups: events.filter(e => e.type === 'email-signup').length,
      clicks: events.filter(e => e.type === 'click').length,
      revenue: events
        .filter(e => e.type === 'purchase' && e.metadata?.total)
        .reduce((sum, e) => sum + (e.metadata.total || 0), 0),
      uniqueSessions: new Set(events.map(e => e.sessionId)).size,
      topPages: {},
      topProducts: {}
    };
    
    // Calculate top pages
    events
      .filter(e => e.type === 'page-view')
      .forEach(e => {
        const page = e.metadata?.page || 'unknown';
        summary.topPages[page] = (summary.topPages[page] || 0) + 1;
      });
    
    // Calculate top products
    events
      .filter(e => e.type === 'purchase')
      .forEach(e => {
        const productIds = e.metadata?.productIds || [];
        productIds.forEach(productId => {
          summary.topProducts[productId] = (summary.topProducts[productId] || 0) + 1;
        });
      });
    
    return summary;
  } catch (error) {
    console.error('Error getting analytics summary:', error);
    return null;
  }
};

/**
 * Get real-time analytics (last 24 hours)
 */
export const getRealtimeAnalytics = async () => {
  const endDate = new Date();
  const startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
  return await getAnalyticsSummary(startDate, endDate);
};

/**
 * Get conversion rate
 */
export const getConversionRate = async (startDate, endDate) => {
  try {
    const summary = await getAnalyticsSummary(startDate, endDate);
    if (summary.pageViews === 0) {
      return 0;
    }
    return (summary.purchases / summary.pageViews) * 100;
  } catch (error) {
    console.error('Error calculating conversion rate:', error);
    return 0;
  }
};



